import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Building2, ChevronDown, Eye, EyeOff, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantSelector() {
  const { 
    user, 
    tenantContext, 
    availableTenants, 
    startImpersonation, 
    stopImpersonation 
  } = useAuth();
  const [loading, setLoading] = useState(false);

  // Only show for super_admin
  const isSuperAdmin = user && (user.role === 'super_admin' || user.is_super_admin);
  
  if (!isSuperAdmin) {
    return null;
  }

  // Show a loading state while tenants are being fetched
  if (!availableTenants || availableTenants.length === 0) {
    return (
      <Button
        variant="outline"
        className="flex items-center gap-2"
        disabled
        data-testid="tenant-selector-loading"
      >
        <Building2 className="w-4 h-4" />
        <span className="hidden md:inline">Loading...</span>
      </Button>
    );
  }

  const handleSelectTenant = async (tenantId, tenantName) => {
    if (loading) return;
    
    setLoading(true);
    try {
      await startImpersonation(tenantId);
      toast.success(`Now viewing as ${tenantName}`);
    } catch (error) {
      toast.error('Failed to switch tenant view');
      console.error('Impersonation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStopImpersonation = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      await stopImpersonation();
      toast.success('Returned to Super Admin view');
    } catch (error) {
      toast.error('Failed to exit tenant view');
      console.error('Stop impersonation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const isImpersonating = tenantContext?.is_impersonating;
  const currentTenantName = tenantContext?.tenant_name || 'All Tenants';

  return (
    <div className="flex items-center gap-2">
      {/* Tenant Selector Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={`flex items-center gap-2 h-9 ${isImpersonating ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-slate-200'}`}
            disabled={loading}
            data-testid="tenant-selector-dropdown"
          >
            <Building2 className="w-4 h-4" />
            <span className="hidden md:inline max-w-[150px] truncate text-sm">
              {isImpersonating ? currentTenantName : 'All Tenants'}
            </span>
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-white">
          <DropdownMenuLabel className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Select Business to View
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {/* Super Admin View (All Tenants) */}
          {isImpersonating && (
            <>
              <DropdownMenuItem
                onClick={handleStopImpersonation}
                className="flex items-center gap-2 cursor-pointer text-blue-600 font-medium"
                data-testid="view-all-tenants-option"
              >
                <EyeOff className="w-4 h-4" />
                Exit to Super Admin View
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          
          {/* Tenant List */}
          {availableTenants.length === 0 ? (
            <DropdownMenuItem disabled>
              <span className="text-gray-500">No tenants found</span>
            </DropdownMenuItem>
          ) : (
            availableTenants.map((tenant) => (
              <DropdownMenuItem
                key={tenant.id}
                onClick={() => handleSelectTenant(tenant.id, tenant.business_name)}
                className={`flex items-center justify-between cursor-pointer ${
                  tenantContext?.tenant_id === tenant.id ? 'bg-blue-50 text-blue-700' : ''
                }`}
                data-testid={`tenant-option-${tenant.id}`}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  <span className="truncate max-w-[160px]">{tenant.business_name}</span>
                </div>
                {tenant.subscription_status === 'suspended' && (
                  <AlertTriangle className="w-4 h-4 text-amber-500" title="Suspended" />
                )}
                {tenantContext?.tenant_id === tenant.id && (
                  <Eye className="w-4 h-4 text-blue-600" />
                )}
              </DropdownMenuItem>
            ))
          )}
          
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-gray-500 font-normal">
            {availableTenants.length} business{availableTenants.length !== 1 ? 'es' : ''} registered
          </DropdownMenuLabel>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
